import { useMemo, useState } from 'react';
import { useVoucherListQuery, useVoucherTypesQuery } from '@/client/api/voucher/voucher.query';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import { Skeleton } from '@/components/ui/skeleton';
import VoucherDetailsDrawer from '@/client/components/admin/Voucher/VoucherDetailsDrawer';
import {
  deriveVoucherStatus,
  formatVoucherValue,
  type VoucherDisplayStatus,
} from '@/client/types/voucher';

const fmtDate = (v?: string | null) => (v ? new Date(v).toLocaleDateString() : '—');

const STATUS_TONE: Record<VoucherDisplayStatus, string> = {
  active: 'bg-emerald-50 text-emerald-700',
  inactive: 'bg-gray-100 text-gray-600',
  expired: 'bg-orange-50 text-orange-700',
  exhausted: 'bg-purple-50 text-purple-700',
  revoked: 'bg-red-50 text-red-700',
};

const VouchersTab = ({ uid }: { uid: number }) => {
  const { data, isLoading } = useVoucherListQuery({ uid_assigned: uid });
  const { data: types = [] } = useVoucherTypesQuery();
  const vouchers = data?.vouchers ?? [];
  const valueTypeById = useMemo(
    () => new Map(types.map((t) => [t.vtid, String(t.value_type)])),
    [types],
  );

  const editPerm = useHasPermission('voucher.edit');
  const revokePerm = useHasPermission('voucher.revoke');
  const deletePerm = useHasPermission('voucher.delete');
  const [selectedVid, setSelectedVid] = useState<number | null>(null);

  if (isLoading) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-36 w-full rounded-xl" />
        ))}
      </div>
    );
  }

  if (vouchers.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-gray-200 bg-white py-16 text-center text-sm text-gray-400">
        No vouchers assigned to this user.
      </div>
    );
  }

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {vouchers.map(v => {
          const status = deriveVoucherStatus(v);
          const redemptions =
            v.max_redemptions != null
              ? `${v.redemption_count ?? 0}/${v.max_redemptions}`
              : String(v.redemption_count ?? 0);
          return (
            <button
              key={v.vid}
              type="button"
              onClick={() => setSelectedVid(v.vid)}
              className="flex flex-col rounded-xl border border-gray-200 bg-white p-5 text-left shadow-sm transition-colors hover:border-blue-200 hover:bg-blue-50/30"
            >
              <div className="mb-2 flex w-full items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate font-semibold text-gray-900">{v.name ?? v.code}</p>
                  <p className="font-mono text-xs text-gray-400">{v.code}</p>
                </div>
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium capitalize ${STATUS_TONE[status]}`}>
                  {status}
                </span>
              </div>

              <p className="text-2xl font-bold text-gray-900">
                {formatVoucherValue(v.value, valueTypeById.get(v.vtid), v.ccode)}
              </p>

              {v.description && (
                <p className="mt-1 line-clamp-2 text-sm text-gray-500">{v.description}</p>
              )}

              <dl className="mt-3 w-full space-y-1 border-t border-gray-100 pt-3 text-xs text-gray-500">
                <div className="flex justify-between">
                  <dt>Valid until</dt>
                  <dd className="text-gray-700">{fmtDate(v.date_valid_until)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt>Redemptions</dt>
                  <dd className="text-gray-700">{redemptions}</dd>
                </div>
                {v.min_purchase_amount != null && (
                  <div className="flex justify-between">
                    <dt>Min. purchase</dt>
                    <dd className="text-gray-700">
                      {v.min_purchase_amount} {v.ccode}
                    </dd>
                  </div>
                )}
              </dl>
            </button>
          );
        })}
      </div>

      <VoucherDetailsDrawer
        isOpen={selectedVid != null}
        setIsOpen={(open) => !open && setSelectedVid(null)}
        vid={selectedVid}
        types={types}
        canEdit={editPerm.isSuperAdmin || editPerm.hasPermission}
        canRevoke={revokePerm.isSuperAdmin || revokePerm.hasPermission}
        canDelete={deletePerm.isSuperAdmin || deletePerm.hasPermission}
        onClose={() => setSelectedVid(null)}
      />
    </>
  );
};

export default VouchersTab;
